import { connect, useDispatch } from 'react-redux';
import { Dispatch, useMemo } from 'react';
import cn from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';

import { DECISION_REASON } from 'CorePackage/MainModule/routes/routes';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getDecisionFormRef,
  getDecisionIsNew,
  getDecisionOrdersSelectOptions,
  getDecisionReasonTypesSelectOptions,
  getDecisionWatchOnlyByTestUser,
  getKnmIsDecisionStatusFillInProcess,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { IKnmReasonTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmReasonTypeSelectOption';
import { IDecisionFormReason } from 'DecisionPackage/MainModule/interfaces/IDecisionFormReason';
import { getReasonBlockId } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { IDictionaryVersion } from 'APIPackage/DictionariesModule/interfaces/IDictionaryVersion';
import { fetchDictionaryValue } from 'APIPackage/DictionariesModule/services/dictionariesApi';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { isAssignmentRequiredByType } from 'KnmPackage/MainModule/common/isAssignmentRequiredByType';
import { IKnmOrderSelectOption } from 'KnmPackage/MainModule/interfaces/IKnmOrderSelectOption';
import { decisionGetOrders } from 'DecisionPackage/MainModule/actions/decisionActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { getFullNameGuarantor } from 'KnmPmPackage/PmReasonsModule/common/getFullNameGuarantor';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { KnmReasonRiskIndicators } from 'KnmPackage/KnmReasonRiskIndicatorModule/components/KnmReasonRiskIndicators';
import { dictionariesApplicationConditionSubjectsItemGet } from 'CorePackage/DictionariesModule/actions/dictionariesActions';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import { DecisionReasonDocument } from './DecisionReasonDocument';
import styles from './DecisionReason.module.scss';
import { DecisionReasonLinks } from './DecisionReasonLinks';
import { getTitleFieldWithRedStar } from '../../../../functions/getTitleFieldWithRedStar';

const defaultReason: IDecisionFormReason = {
  reason: undefined,
  type: undefined,
  text: undefined,
  date: undefined,
  riskIndicators: undefined,
  requirementDetails: undefined,
  dateRequirement: undefined,
  assignmentNumber: undefined,
  assignmentDate: undefined,
  numGuid: undefined,
  dateCreate: undefined,
  dateBegin: undefined,
  dateEnd: undefined,
  order: undefined,
  ervkUuid: undefined,
  links: undefined,
  listId: uuidv4(),
  fullName: undefined,
  indicatorRiskAttachment: undefined,
  indicatorRiskAuto: undefined,
  lastConductedKnmDate: undefined,
  prescriptionDate: undefined,
  prescriptionNumber: undefined,
  programAttachment: undefined,
  programNumber: undefined,
  prosecutorAttachment: undefined,
};

interface IDecisionReasonProps {
  created: string | undefined;
}

interface IDecisionReasonStoreProps {
  isNewKnm: boolean;
  isSupervisor: boolean;
  isDecisionStatusFillInProcess: boolean;
  erknmDecisionReasonTypesSelectOptions: IKnmReasonTypeSelectOption[];
  formRef: IDecisionFormRef | undefined;
  decisionOrders: IKnmOrderSelectOption[] | undefined;
  isDecisionWatchOnlyByTestUser: boolean;
}

const DecisionReasonWithoutStore = ({
  formRef,
  isNewKnm,
  isSupervisor,
  isDecisionStatusFillInProcess,
  erknmDecisionReasonTypesSelectOptions,
  decisionOrders,
  isDecisionWatchOnlyByTestUser,
  created,
}: IDecisionReasonProps & IDecisionReasonStoreProps) => {
  const remove = formRef?.current.mutators.remove;
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const isDisabled = !isSupervisor || (!isNewKnm && !isDecisionStatusFillInProcess) || isDecisionWatchOnlyByTestUser;

  const typesSelectOptions = useMemo(() => {
    return erknmDecisionReasonTypesSelectOptions;
  }, [erknmDecisionReasonTypesSelectOptions]);

  const { kindControl, knoOrganization, kind } = formRef?.current.getState().values ?? {};

  const handleReasonTypeChange = async (value: IKnmReasonTypeSelectOption) => {
    if (value?.ervkUuid) {
      const dictionaryValue: IDictionaryVersion = await fetchDictionaryValue(DictionaryUUIDsEnum.ERVK, value.ervkUuid);

      const reasonRequirementsValue = JSON.parse(dictionaryValue.value);

      formRef?.current.change(`reasonRequirements` as keyof IDecisionForm, reasonRequirementsValue);
      dispatch(dictionariesApplicationConditionSubjectsItemGet(value?.ervkUuid));
    }

    formRef?.current.change('reasons', [
      {
        ...defaultReason,
        type: value,
        ervkUuid: value?.ervkUuid,
      },
    ]);

    formRef?.current.change('isOrganizationNew');
    formRef?.current.change('organization');
    formRef?.current.change('objects');
    formRef?.current.change('events');
  };

  const getOrderOptions = (reasonFieldValue: IDecisionFormReason) => () => {
    if (
      knoOrganization &&
      kindControl &&
      kind &&
      reasonFieldValue &&
      isAssignmentRequiredByType(reasonFieldValue.type)
    ) {
      const currentErvkUuid = reasonFieldValue?.ervkUuid;

      dispatch(
        decisionGetOrders(
          String(knoOrganization?.value.dictVersionId),
          String(kindControl?.value.dictVersionId),
          String(currentErvkUuid),
          kind.value.dictVersionId,
        ),
      );
    }
  };

  const handleOrderChange = (index: number) => (value?: IKnmOrderSelectOption) => {
    const { number, date, guarantors, recordVersionId } = value || {};

    formRef?.current.change('orderRequirements', value);

    // @ts-ignore
    const objects: IKnmErknmFormObject[] | undefined = formRef?.current.getFieldState('objects' as keyof IDecisionForm)
      ?.value;

    objects?.forEach((_item, i) => {
      remove && remove('objects', i);
    });

    formRef?.current.change('organization');

    formRef?.current.change(`reasons[${index}].assignmentNumber` as keyof IDecisionForm, number);
    formRef?.current.change(`reasons[${index}].assignmentDate` as keyof IDecisionForm, date);
    formRef?.current.change(`reasons[${index}].fullName` as keyof IDecisionForm, getFullNameGuarantor(guarantors?.[0]));

    dispatch(dictionariesApplicationConditionSubjectsItemGet(undefined, recordVersionId));
  };

  const isAssignment = (reason: IDecisionFormReason) => {
    return !!reason.order;
  };

  const makeRemoveReasonOrder = (index: number) => () => {
    formRef?.current.change(`reasons[${index}].order` as keyof IDecisionForm);
    handleOrderChange(index)();
  };

  return (
    <>
      <div className={styles.ReasonFieldBlock} id={DECISION_REASON}>
        <div className="shared-group-margin">
          <label className="shared-group-label" htmlFor="knoOrganizationPm">
            {getTitleFieldWithRedStar({
              label: 'Основание проведения мероприятия',
              // лайфхак для основания - потому что field array всегда уже с 1 значением по умолчанию
              field: `reasons[0].type`,
              sectionName: 'reasons[0].type',
              isDecision: true,
            })}
          </label>
        </div>

        <FieldArray name="reasons">
          {({ fields }: FieldArrayRenderProps<IDecisionFormReason, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return (
              <ul className={styles.ReasonsList}>
                {fields.map((reasonsFieldName, sIndex) => {
                  const reasonsFieldsValue: IDecisionFormReason[] = fields.value;
                  const reasonFieldValue: IDecisionFormReason = reasonsFieldsValue?.[sIndex];
                  const id = getReasonBlockId(reasonFieldValue.listId || '');
                  const { order } = reasonFieldValue;

                  const isActiveOrderHelpText =
                    order?.flagInagency || order?.flagIt || order?.flagMsp || order?.flagSonko;

                  return (
                    <li id={id} key={id}>
                      <div className="shared-field-row">
                        <div className={cn(['shared-deleting-row', styles.WrapperReasons])}>
                          <SelectInputField
                            disabled={isDisabled}
                            id={`${reasonsFieldName}.type`}
                            isShowTooltip={true}
                            isSearchable
                            name={`${reasonsFieldName}.type`}
                            options={typesSelectOptions}
                            onChange={handleReasonTypeChange}
                            placeholder={'Выберите основание проведения мероприятия'}
                            size="medium"
                            tooltipWidth={document.getElementById(`${reasonsFieldName}.type`)?.offsetWidth}
                          />
                          {reasonFieldValue?.type?.hasText && (
                            <FieldBlock label="" color="black" margin="top">
                              <TextareaField
                                className={styles.ReasonTextField}
                                name={`${reasonsFieldName}.text`}
                                disabled={isDisabled}
                                placeholder={getTitleFieldWithRedStar({
                                  label: 'Введите иное основание',
                                  field: `${reasonsFieldName}.text`,
                                  isDecision: true,
                                })}
                              />
                            </FieldBlock>
                          )}
                          {(reasonFieldValue?.type?.programNumberRequired ||
                            reasonFieldValue?.type?.programFileRequired) && (
                            <FieldBlock margin="top">
                              <div className="shared-field-row">
                                <div className="shared-row-flex">
                                  {reasonFieldValue?.type?.programNumberRequired && (
                                    <Row>
                                      <FieldBlock
                                        label={getTitleFieldWithRedStar({
                                          label: 'Номер программы проверок',
                                          field: `${reasonsFieldName}.programNumber`,
                                          isDecision: true,
                                        })}
                                        placementLabel="left"
                                        color="black"
                                      >
                                        <TextInputField
                                          className="shared-row-field"
                                          id={`${reasonsFieldName}.programNumber`}
                                          name={`${reasonsFieldName}.programNumber`}
                                          disabled={isDisabled}
                                        />
                                      </FieldBlock>
                                    </Row>
                                  )}
                                  {reasonFieldValue?.type?.programFileRequired && (
                                    <DecisionReasonDocument
                                      watchOnly={isDisabled}
                                      name={`${reasonsFieldName}.programAttachment`}
                                      attachment={reasonFieldValue?.programAttachment}
                                      formRef={formRef}
                                      errorFieldKey={'programAttachment'}
                                      title={getTitleFieldWithRedStar({
                                        label: 'Файл программы проверок',
                                        field: `${reasonsFieldName}.programAttachment`,
                                        isDecision: true,
                                      })}
                                    />
                                  )}
                                </div>
                              </div>
                            </FieldBlock>
                          )}
                          {reasonFieldValue?.type?.indicatorRiskRequired && (
                            <KnmReasonRiskIndicators
                              reasonFieldValue={reasonFieldValue}
                              reasonsFieldName={reasonsFieldName}
                              isDecision={true}
                              isPm={false}
                              formRef={formRef}
                              isWatchOnly={isDisabled}
                            />
                          )}
                          {(reasonFieldValue?.type?.prescriptionDateRequired ||
                            reasonFieldValue?.type?.prescriptionNumberRequired) && (
                            <Row margin="none">
                              {reasonFieldValue?.type?.prescriptionDateRequired && (
                                <div className={cn(['shared-field-row', styles.BasisDocumentStartDateBlock])}>
                                  <div className="shared-field-row-block_width">
                                    <label className="shared-field-label_row">
                                      {getTitleFieldWithRedStar({
                                        label: 'Дата документа',
                                        field: `${reasonsFieldName}.prescriptionDate`,
                                        isDecision: true,
                                      })}
                                    </label>
                                  </div>
                                  <DatePickerField
                                    className="shared-row-field"
                                    id={`${reasonsFieldName}.prescriptionDate`}
                                    name={`${reasonsFieldName}.prescriptionDate`}
                                    disabled={isDisabled}
                                    validate={makeRequired('Поле не может быть пустым')}
                                  />
                                </div>
                              )}

                              {reasonFieldValue?.type?.prescriptionNumberRequired && (
                                <div className={cn(['shared-field-row', styles.BasisDocumentNumberBlock])}>
                                  <div className="shared-field-row-block_width">
                                    <label className="shared-field-label_row">
                                      {getTitleFieldWithRedStar({
                                        label: 'Номер документа',
                                        field: `${reasonsFieldName}.prescriptionNumber`,
                                        isDecision: true,
                                      })}
                                    </label>
                                  </div>
                                  <Row align="center">
                                    <TextInputField
                                      id={`${reasonsFieldName}.prescriptionNumber`}
                                      name={`${reasonsFieldName}.prescriptionNumber`}
                                      disabled={isDisabled}
                                    />
                                  </Row>
                                </div>
                              )}
                            </Row>
                          )}
                          {(reasonFieldValue?.type?.dateRequirement ||
                            reasonFieldValue?.type?.detailsRequirement ||
                            reasonFieldValue?.type?.prosecutorAttachmentRequired) && (
                            <>
                              <Row margin="none">
                                {reasonFieldValue?.type?.dateRequirement && (
                                  <div className={cn(['shared-field-row', styles.BasisDocumentStartDateBlock])}>
                                    <div className="shared-field-row-block_width">
                                      <label className="shared-field-label_row">
                                        {getTitleFieldWithRedStar({
                                          label: 'Дата требования',
                                          field: `${reasonsFieldName}.dateRequirement`,
                                          isDecision: true,
                                        })}
                                      </label>
                                    </div>
                                    <DatePickerField
                                      className="shared-row-field"
                                      id={`${reasonsFieldName}.dateRequirement`}
                                      name={`${reasonsFieldName}.dateRequirement`}
                                      disabled={isDisabled}
                                      validate={makeRequired('Поле не может быть пустым')}
                                    />
                                  </div>
                                )}

                                {reasonFieldValue?.type?.detailsRequirement && (
                                  <div className={cn(['shared-field-row', styles.BasisDocumentNumberBlock])}>
                                    <div className="shared-field-row-block_width">
                                      <label className="shared-field-label_row">
                                        {getTitleFieldWithRedStar({
                                          label: 'Реквизиты требования',
                                          field: `${reasonsFieldName}.requirementDetails`,
                                          isDecision: true,
                                        })}
                                      </label>
                                    </div>
                                    <Row align="center">
                                      <TextInputField
                                        id={`${reasonsFieldName}.requirementDetails`}
                                        name={`${reasonsFieldName}.requirementDetails`}
                                        disabled={isDisabled}
                                      />
                                    </Row>
                                  </div>
                                )}
                              </Row>

                              {reasonFieldValue?.type?.prosecutorAttachmentRequired && (
                                <Row margin="none">
                                  <DecisionReasonDocument
                                    watchOnly={isDisabled}
                                    name={`${reasonsFieldName}.prosecutorAttachment`}
                                    attachment={reasonFieldValue?.prosecutorAttachment}
                                    formRef={formRef}
                                    errorFieldKey={'prosecutorAttachment'}
                                    title={getTitleFieldWithRedStar({
                                      label: 'Файл, содержащий требование прокурора',
                                      field: `${reasonsFieldName}.prosecutorAttachment`,
                                      isDecision: true,
                                    })}
                                  />
                                </Row>
                              )}
                            </>
                          )}
                          {isAssignmentRequiredByType(reasonFieldValue.type) && (
                            <FieldBlock margin="top">
                              <div className="shared-field-row">
                                <div className="shared-row-flex">
                                  {!order && (
                                    <Row>
                                      <FieldBlock
                                        label={
                                          <>
                                            {getTitleFieldWithRedStar({
                                              label: 'Выберите поручение',
                                              field: `${reasonsFieldName}.order`,
                                              isDecision: true,
                                            })}

                                            <Tooltip
                                              placement="right"
                                              type="info"
                                              content={
                                                <div className="shared-tooltip-info-content">
                                                  Перечень поручений наполняется Министерством экономического развития
                                                  Российской Федерации.
                                                  <br />В случае отсутствия необходимого поручения рекомендуем направить
                                                  информацию о нем в адрес Министерства
                                                </div>
                                              }
                                            >
                                              <span className="shared-tooltip-info-2-icon" />
                                            </Tooltip>
                                          </>
                                        }
                                        placementLabel="left"
                                        color="black"
                                        labelSize="col-4"
                                      >
                                        <SelectInputField
                                          className="shared-row-field"
                                          id={`${reasonsFieldName}.order`}
                                          name={`${reasonsFieldName}.order`}
                                          isSearchable
                                          disabled={isDisabled}
                                          options={decisionOrders}
                                          onOpen={getOrderOptions(reasonFieldValue)}
                                          onChange={handleOrderChange(sIndex)}
                                          size="medium"
                                          placeholder="Введите номер, дату или полное наименование поручения"
                                        />
                                      </FieldBlock>
                                      {isActiveOrderHelpText && (
                                        <Hint text="Статус лица в реестре противоречит требованиям поручения" />
                                      )}
                                    </Row>
                                  )}
                                  {isAssignment(reasonFieldValue) && (
                                    <>
                                      <Row notWrap>
                                        <FieldBlock
                                          size="half"
                                          label={getTitleFieldWithRedStar({
                                            label: 'Номер поручения',
                                            field: `${reasonsFieldName}.assignmentNumber`,
                                            isDecision: true,
                                          })}
                                          placementLabel="left"
                                          color="black"
                                          margin="right"
                                        >
                                          <TextInputField
                                            className="shared-row-field"
                                            id={`${reasonsFieldName}.assignmentNumber`}
                                            name={`${reasonsFieldName}.assignmentNumber`}
                                            disabled={true}
                                          />
                                        </FieldBlock>
                                        <FieldBlock
                                          size="half"
                                          label={getTitleFieldWithRedStar({
                                            label: 'Дата поручения',
                                            field: `${reasonsFieldName}.assignmentDate`,
                                            isDecision: true,
                                          })}
                                          placementLabel="left"
                                          color="black"
                                          margin="right"
                                        >
                                          <TextInputField
                                            className="shared-row-field"
                                            id={`${reasonsFieldName}.assignmentDate`}
                                            name={`${reasonsFieldName}.assignmentDate`}
                                            disabled={true}
                                          />
                                        </FieldBlock>
                                      </Row>
                                      <Row>
                                        <FieldBlock
                                          label={getTitleFieldWithRedStar({
                                            label: 'ФИО лица, издавшего поручение',
                                            field: `${reasonsFieldName}.fullName`,
                                            isDecision: true,
                                          })}
                                          color="black"
                                          placementLabel="left"
                                          labelSize="col-4"
                                        >
                                          <TextInputField
                                            className="shared-row-field"
                                            name={`${reasonsFieldName}.fullName`}
                                            disabled={true}
                                          />
                                        </FieldBlock>
                                      </Row>
                                    </>
                                  )}
                                </div>
                                {order && !isDisabled && (
                                  <CloseButton
                                    className="shared-deleting-row-btn"
                                    onClick={!isDisabled && makeRemoveReasonOrder(sIndex)}
                                  />
                                )}
                              </div>
                            </FieldBlock>
                          )}
                          {reasonFieldValue?.type?.linkOther && (
                            <DecisionReasonLinks
                              watchOnly={isDisabled}
                              isEnabled={true}
                              linksName={`${reasonsFieldName}.links`}
                              created={created}
                              reason={reasonFieldValue}
                              kindControlCode={kindControl?.code || ''}
                              controllingOrganizationIds={knoOrganization?.idStr?.toString() || ''}
                            />
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          }}
        </FieldArray>
      </div>
    </>
  );
};

const mapStateToProps = (state: IRootState): IDecisionReasonStoreProps => ({
  formRef: getDecisionFormRef(state),
  isNewKnm: getDecisionIsNew(state) || false,
  isSupervisor: getUserRoleIsSupervisor(state),
  isDecisionStatusFillInProcess: getKnmIsDecisionStatusFillInProcess(state),
  erknmDecisionReasonTypesSelectOptions: getDecisionReasonTypesSelectOptions(state),
  decisionOrders: getDecisionOrdersSelectOptions(state),
  isDecisionWatchOnlyByTestUser: getDecisionWatchOnlyByTestUser(state),
});

export const DecisionReason = connect(mapStateToProps)(DecisionReasonWithoutStore);
